import React, { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from '@sentry/react';
import { ReactCookieProps, withCookies } from 'react-cookie';
import ErrorPage from 'routes/ErrorPage';
import { UserToken } from '../../config/auth';
import { sentryEnabled } from '../../utils/sentryConfig';
import jwtDecode from "jwt-decode";

interface Props extends ReactCookieProps {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

const TOKEN_NAME = import.meta.env.REACT_APP_AUTH_TOKEN_COOKIE_NAME || 'cfc.auth_token';
const SEND_DEFAULT_PII = import.meta.env.REACT_APP_SENTRY_SEND_DEFAULT_PII === 'true';

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(_: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (!sentryEnabled) {
      // Sentry inactive (local-like env or no DSN): still surface the error so it's diagnosable.
      console.error('Uncaught error:', error, errorInfo.componentStack);
      return;
    }

    const token = this.props.cookies?.get(TOKEN_NAME);
    let user: UserToken | null = null;

    if (token) {
      try {
        user = jwtDecode(token);
      } catch (_decodeError) {
        // Ignore malformed/stale tokens so the fallback UI can still render.
        user = null;
      }
    }

    Sentry.withScope((scope) => {
      scope.setExtra('componentStack', errorInfo.componentStack);

      if (user) {
        scope.setUser(
          SEND_DEFAULT_PII
            ? {
                id: String(user.id),
                email: user.email,
                username: [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || undefined,
              }
            : {
                id: String(user.id),
              }
        );
      }

      Sentry.captureException(error);
    });

  }

  public render() {
    if (this.state.hasError) {
      // Render fallback UI, serving up the error page.

      return <ErrorPage />
    }

    return this.props.children;
  }
}

export default withCookies(ErrorBoundary);
